import { useState, useEffect } from "react";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Loader2, Download, FileText, Shield, CheckCircle2, Building2, ArrowLeft } from "lucide-react";
import { Link } from "wouter";

export default function SaasContractPage() {
  const [contractText, setContractText] = useState<string>("");
  const [isLoading, setIsLoading] = useState(true);
  const [isDownloading, setIsDownloading] = useState(false);

  // Buscar dados do salão logado para preencher automaticamente o contrato
  const { data: salon } = trpc.salon.getMySalon.useQuery(undefined, { retry: false });
  const salonId = (salon as any)?.id ?? null;

  useEffect(() => {
    const url = salonId ? `/api/contract/saas/preview?salonId=${salonId}` : "/api/contract/saas/preview";
    fetch(url)
      .then((r) => r.json())
      .then((data) => {
        setContractText(data.text ?? "");
        setIsLoading(false);
      })
      .catch(() => setIsLoading(false));
  }, [salonId]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const dlUrl = salonId ? `/api/contract/saas/download?salonId=${salonId}` : "/api/contract/saas/download";
      const response = await fetch(dlUrl);
      if (!response.ok) throw new Error("Erro ao baixar");
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      const salonSlug = (salon as any)?.slug ?? "contrato";
      link.download = `contrato-saas-${salonSlug}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch {
      alert("Erro ao baixar o contrato. Tente novamente.");
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <div className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link href="/">
              <Button variant="ghost" size="sm" className="gap-2">
                <ArrowLeft className="w-4 h-4" />
                Voltar
              </Button>
            </Link>
            <div className="h-5 w-px bg-border" />
            <div className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-primary" />
              <span className="font-semibold text-sm">Beleza e Bronze</span>
            </div>
          </div>
          <Button
            onClick={handleDownload}
            disabled={isDownloading || isLoading}
            size="sm"
            className="gap-2"
          >
            {isDownloading ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Download className="w-4 h-4" />
            )}
            Baixar PDF
          </Button>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 py-10 space-y-8">
        {/* Título */}
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 bg-primary/10 text-primary px-4 py-1.5 rounded-full text-sm font-medium">
            <Shield className="w-4 h-4" />
            Documento Oficial
          </div>
          <h1 className="text-3xl font-[Cormorant_Garamond,serif] font-semibold text-foreground">
            Contrato de Licença de Uso
          </h1>
          <p className="text-muted-foreground text-base max-w-xl mx-auto">
            Contrato B2B entre a <strong>Beleza e Bronze Tecnologia</strong> (CNPJ 40.619.126/0001-63)
            e os salões que contratam a plataforma de agendamento.
          </p>
        </div>

        {/* Destaques do contrato */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { icon: Shield, label: "Proteção da Fornecedora", desc: "Limitação de responsabilidade" },
            { icon: FileText, label: "14 Cláusulas", desc: "Cobertura jurídica completa" },
            { icon: CheckCircle2, label: "LGPD Compliance", desc: "Controlador × Operador" },
            { icon: Building2, label: "Propriedade Intelectual", desc: "Código e marca protegidos" },
          ].map(({ icon: Icon, label, desc }) => (
            <div key={label} className="bg-card border border-border rounded-lg p-4 text-center space-y-1">
              <Icon className="w-5 h-5 text-primary mx-auto" />
              <p className="text-xs font-semibold text-foreground">{label}</p>
              <p className="text-xs text-muted-foreground">{desc}</p>
            </div>
          ))}
        </div>

        {/* Texto do contrato */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="bg-secondary/30 border-b border-border px-6 py-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4 text-muted-foreground" />
              <span className="text-sm font-medium text-foreground">Contrato B2B SaaS — Beleza e Bronze</span>
            </div>
            <span className="text-xs text-muted-foreground">CNPJ 40.619.126/0001-63</span>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-20">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : (
            <div className="p-6 md:p-10">
              <pre className="whitespace-pre-wrap font-mono text-xs text-foreground/80 leading-relaxed">
                {contractText}
              </pre>
            </div>
          )}
        </div>

        {/* Botão de download final */}
        <div className="flex flex-col items-center gap-3 pt-4 pb-10">
          <Button
            onClick={handleDownload}
            disabled={isDownloading || isLoading}
            size="lg"
            className="gap-2 px-8"
          >
            {isDownloading ? (
              <Loader2 className="w-5 h-5 animate-spin" />
            ) : (
              <Download className="w-5 h-5" />
            )}
            Baixar Contrato em PDF
          </Button>
          <p className="text-xs text-muted-foreground text-center max-w-sm">
            O PDF gerado inclui os dados do seu estabelecimento e está pronto para assinatura.
            Para dúvidas, entre em contato: <strong>contato@belezaebronze.club</strong>
          </p>
        </div>
      </div>
    </div>
  );
}
